import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, GOOGLE_CLIENT_ID } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { trackEvent } from '../../services/analyticsService';
import GoogleSsoButton from './GoogleSsoButton';

interface LocationState {
  message?: string;
  from?: string;
}

const KitchenMark: React.FC = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <path d="M7 12.5h14c-.5 4-3.1 6.2-7 6.2s-6.5-2.2-7-6.2Z" />
    <path d="M4.8 12.5h18.4M9.2 9.2c1.1-.2 2.2-.9 2.8-2 .7-1.2 1.3-1.8 2-1.8s1.3.6 2 1.8c.6 1.1 1.7 1.8 2.8 2M11.5 21.8h5" />
  </svg>
);

const IntegrationCloud: React.FC = () => (
  <div className="integration-cloud" aria-hidden="true">
    <div className="integration-tile integration-tile--sheets">
      <svg viewBox="0 0 42 48">
        <path d="M8 2h19l9 9v35H8z" fill="#18864b" />
        <path d="M27 2v10h9" fill="#8ccaa9" />
        <path d="M14 20h17v17H14zM14 25.7h17M14 31.3h17M19.7 20v17M25.3 20v17" fill="none" stroke="white" strokeWidth="2" />
      </svg>
    </div>
    <div className="integration-tile integration-tile--word"><span>W</span></div>
    <div className="integration-tile integration-tile--notion"><span>N</span></div>
    <div className="integration-tile integration-tile--hubspot">
      <svg viewBox="0 0 48 48"><path d="M17 27 28 16m0 0 7 7M28 16V9M17 27l-7-7m7 7v10m0-10h18" /><circle cx="17" cy="39" r="4" /><circle cx="8" cy="18" r="4" /><circle cx="28" cy="7" r="4" /><circle cx="37" cy="25" r="4" /></svg>
    </div>
    <div className="integration-tile integration-tile--gmail">
      <svg viewBox="0 0 54 42"><path d="M4 37V8l23 18L50 8v29" /><path d="m4 8 9-4 14 11L41 4l9 4" /></svg>
    </div>
    <div className="integration-tile integration-tile--openai">
      <svg viewBox="0 0 48 48"><path d="M24 9a8 8 0 0 1 14 6v3a8 8 0 0 1 1 14l-3 2a8 8 0 0 1-14 7l-3-2A8 8 0 0 1 6 31v-4a8 8 0 0 1 1-14l3-2a8 8 0 0 1 14-2Z" /><path d="m15 18 9-5 9 5v11l-9 5-9-5Zm0 0v11l9 5m9-16-9 5-9-5m9-5v10" /></svg>
    </div>
    <div className="integration-tile integration-tile--spark">
      <svg viewBox="0 0 50 50"><path d="M25 3v44M3 25h44M9 9l32 32M41 9 9 41M14 4l22 42M46 14 4 36M36 4 14 46M4 14l42 22" /></svg>
    </div>
  </div>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const isGoogleSsoEnabled = !!GOOGLE_CLIENT_ID;
  
  // Keep track of where the user came from, to redirect after login
  const [redirectPath, setRedirectPath] = useState('/');

  useEffect(() => {
    // Check for message in location state (from password reset, etc.)
    const state = location.state as LocationState;
    
    if (state?.message) {
      setSuccess(state.message);
    }
    
    // Store the redirect path if it exists
    if (state?.from) {
      setRedirectPath(state.from);
      console.log(`User will be redirected to: ${state.from} after login`);
    }
    
    // Clear the state to prevent showing the message on refresh
    if (state?.message || state?.from) {
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    
    setIsResendingVerification(true);
    setError('');
    setSuccess('');
    setAccountNotFound(false);
    
    try {
      const resendUrl = `${API_BASE_URL}/auth/resend-verification`;
      const response = await fetch(resendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: unverifiedEmail }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Verification email has been resent. Please check your inbox.');
      } else {
        setError(data.message || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('An error occurred while resending the verification email');
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    setAccountNotFound(false);
    setNeedsVerification(false);

    try {
      // Use the API base URL from config
      const loginUrl = `${API_BASE_URL}/auth/login`;
      console.log('Login URL:', loginUrl);
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Use AuthContext's login function and wait for a small delay
        // to ensure the token is properly set in localStorage and AuthContext
        login(data.token);
        
        // Track successful login event
        trackEvent('user_login', {
          redirect_path: redirectPath,
          email_domain: email.split('@')[1]
        });
        
        // Set a short delay before navigation to ensure auth state is updated
        setTimeout(() => {
          console.log(`Redirecting user to: ${redirectPath}`);
          navigate(redirectPath);
        }, 100);
      } else if (response.status === 403 && data.requiresVerification) {
        // Email requires verification
        setNeedsVerification(true);
        setUnverifiedEmail(data.email || email);
        setError(data.message);
      } else {
        setAccountNotFound(data.code === 'ACCOUNT_NOT_FOUND');
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setAccountNotFound(false);
      setError('An error occurred during login');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = (token: string) => {
    login(token);

    trackEvent('user_login', {
      redirect_path: redirectPath,
      provider: 'google'
    });

    setTimeout(() => {
      navigate(redirectPath);
    }, 100);
  };

  return (
    <div className="relative isolate h-screen h-[100dvh] w-full overflow-y-auto bg-stone-950">
      <div className="pointer-events-none fixed inset-0">
        <div
          className="login-background h-full w-full"
          style={{
            '--login-background-mobile': 'url(/kitchen-login-poster.webp)',
            '--login-background-desktop': 'url(/kitchen-login-poster-wide.webp)',
          } as React.CSSProperties}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0.16))' }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(0, 0, 0, 0.08), transparent, rgba(0, 0, 0, 0.03))' }}
        />
      </div>

      <div className="relative z-10 mx-auto grid min-h-screen min-h-[100dvh] w-full max-w-7xl items-center gap-8 px-3 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,448px)] lg:gap-12 lg:px-8">
        <section className="flex flex-col gap-8 text-white lg:min-h-[calc(100vh-4rem)]">
          <div className="flex items-center gap-3 text-lg font-semibold tracking-wide">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-blue-500 shadow-lg shadow-black/25" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M5 10h14v7a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-7Z" />
                <path d="M3 10h18M8 6h8M10 3h4" />
              </svg>
            </span>
            <span>Kitchen</span>
          </div>

          <div className="max-w-2xl rounded-2xl border border-white/15 bg-black/30 p-6 shadow-2xl shadow-black/20 backdrop-blur-sm sm:p-8 lg:my-auto">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Automation recipes anyone can make.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-gray-100 sm:text-xl">
              Use plain english to describe the steps in the recipe, let the kitchen take care of the test.
            </p>
          </div>
        </section>

        <section className="w-full rounded-2xl bg-white/95 p-4 text-gray-900 shadow-2xl shadow-black/30 backdrop-blur-md dark:bg-stone-900/95 dark:text-gray-100 sm:p-8" aria-labelledby="login-title">
          <h2 id="login-title" className="text-3xl font-bold tracking-tight">Welcome back</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Sign in to keep cooking.</p>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div
                role="alert"
                className={`rounded-lg border p-4 ${accountNotFound ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}
              >
                {accountNotFound && (
                  <div className="mb-1 text-sm font-semibold text-amber-800">Account not found</div>
                )}
                <div className={`text-sm ${accountNotFound ? 'text-amber-700' : 'text-red-700'}`}>{error}</div>
                {accountNotFound && (
                  <Link
                    to="/register"
                    className="mt-3 inline-flex rounded-md bg-amber-100 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-200"
                  >
                    Create an account
                  </Link>
                )}
                {needsVerification && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResendingVerification}
                      className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isResendingVerification ? 'Sending...' : 'Resend verification email'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {success && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4" role="status">
                <div className="text-sm text-green-700">{success}</div>
              </div>
            )}

            {isGoogleSsoEnabled && (
              <>
                <GoogleSsoButton
                  text="signin_with"
                  intent="login"
                  width="400"
                  onSuccess={handleGoogleSuccess}
                  onError={(message, code) => {
                    setNeedsVerification(false);
                    setSuccess('');
                    setAccountNotFound(code === 'ACCOUNT_NOT_FOUND');
                    setError(message);
                  }}
                />
                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400" aria-hidden="true">
                  <div className="h-px flex-1 bg-gray-200 dark:bg-stone-700" />
                  <span>Or sign in with email</span>
                  <div className="h-px flex-1 bg-gray-200 dark:bg-stone-700" />
                </div>
              </>
            )}

            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</span>
                <input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  className="block w-full rounded-lg border-gray-300 px-3 py-2.5 text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-800 dark:text-gray-100 dark:placeholder-gray-500 sm:text-sm"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center justify-between gap-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span>Password</span>
                  <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                    Forgot your password?
                  </Link>
                </span>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    className="block w-full rounded-lg border-gray-300 px-3 py-2.5 pr-11 text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-stone-700 dark:bg-stone-800 dark:text-gray-100 dark:placeholder-gray-500 sm:text-sm"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <path d="M3 3l18 18" />
                        <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                        <path d="M9.4 5.2A9.3 9.3 0 0 1 12 5c5 0 9 4.5 9 7a13 13 0 0 1-2.2 3M6.3 6.3A13.2 13.2 0 0 0 3 12c0 2.5 4 7 9 7a9.5 9.5 0 0 0 3.3-.6" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-transparent bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-500 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:focus:ring-white dark:focus:ring-offset-stone-900"
            >
              {isSubmitting && (
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z" />
                </svg>
              )}
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>

            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              New to Kitchen?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                Create an account
              </Link>
            </p>
            <div className="flex justify-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <Link to="/terms" className="hover:text-gray-800 dark:hover:text-gray-200">Terms</Link>
              <Link to="/privacy" className="hover:text-gray-800 dark:hover:text-gray-200">Privacy</Link>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Login;
